import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Box, Modal} from '@material-ui/core'
import {FormAction, FormActions} from 'common/utils/form-generation'
import {commonStyles} from 'common/styles/Styles'
import {PATIENT_SERVICE_KEY} from '../../modules/patients'
import {PatientService} from '../../modules/patients/services/PatientService'
import {PatientDeleteInfo, PatientDeleteType} from '../../modules/patients/models/PatientDeleteInfo'
import {getPatientContainer} from '../../container/patient-module'
   
const patientService = getPatientContainer().get<PatientService>(PATIENT_SERVICE_KEY)

export type PatientDeleteModalProps = {
    open: boolean
    patientId?: string
    patientType: PatientDeleteType
    onClose: () => void
    onDeleted: () => void
}




/**
 * Asks the backend what is going to happen before removing a patient from a
 * module: a warning modal when the patient has associated cases (deletion is
 * not possible), a confirmation modal otherwise.
 */
export const PatientDeleteModal = (props: PatientDeleteModalProps) => {
    const {t} = useTranslation()
    const styles = commonStyles()
    const [info, setInfo] = useState<PatientDeleteInfo>()
    const [isLoading, setIsLoading] = useState<boolean>(false)

    useEffect(() => {
        if (!props.open || !props.patientId) {
            return
        }
        setIsLoading(true)
        patientService.getDeleteInfo(props.patientId, props.patientType).subscribe((res) => {
            setIsLoading(false)
            setInfo(res)
        })
    }, [props.open, props.patientId, props.patientType])

    const closeModal = () => {
        setInfo(undefined)
        props.onClose()
    }

    const deleteConfirmed = () => {
        if (!props.patientId) {
            return
        }
        patientService.delete(props.patientId, props.patientType).subscribe((deleted) => {
            setInfo(undefined)
            props.onClose() 
            if (deleted) {
                props.onDeleted()
            }
        })
    }

    const actions: FormAction[] = [
        {
            label: t('close'),
            handleAction: closeModal,
        },
    ]

    const hasCases = !!info && info.hasCases
    // A patient showing up in more than one section is only removed from the
    // current one, otherwise the record is deleted for good.
    const message = !info || isLoading
        ? t('loading')
        : hasCases
        ? t('patientHasCasesMessage', {count: info.casesCount})
        : info.fullDelete
        ? t('patientDeletePermanentConfirmationMessage')
        : t('patientDeleteConfirmationMessage')

    return (
        <Modal open={props.open}>
            <Box className={styles.modal}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (!hasCases) {
                            deleteConfirmed()
                        }
                    }}>
                    <Box mt={2} textAlign={'justify'}>
                        {message}
                    </Box>
                    <FormActions
                        actions={actions}
                        message={t('confirm')}
                        disabled={isLoading || hasCases}
                    />
                </form>
            </Box>
        </Modal>
    )
}